import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { getSchoolUrl } from "@/lib/slug";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { BadgeCheck, Clock, Star, Pencil, ThumbsUp, ThumbsDown, ArrowRight, EyeOff } from "lucide-react";
import { promptLogin, promptRegister } from "@/lib/authRedirect";

const RATING_KEYS = [
  { key: "quality_teaching", label: "Kualitas pengajar" },
  { key: "facility", label: "Fasilitas" },
  { key: "safety", label: "Keamanan" },
  { key: "extracurricular", label: "Ekstrakurikuler" },
  { key: "value_for_money", label: "Nilai harga" },
];

function StatusPill({ status, isVerified }) {
  if (status === "pending") {
    return <span data-testid="my-review-status-pending" className="inline-flex items-center gap-1.5 text-[11px] text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-full px-2.5 py-1"><Clock size={11} /> Menunggu moderasi</span>;
  }
  if (status === "rejected") {
    return <span data-testid="my-review-status-rejected" className="inline-flex items-center gap-1.5 text-[11px] text-[#7F1D1D] bg-[#FEE2E2] border border-[#FCA5A5] rounded-full px-2.5 py-1">Ditolak admin</span>;
  }
  return isVerified ? (
    <span data-testid="my-review-status-verified" className="inline-flex items-center gap-1.5 text-[11px] text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2.5 py-1"><BadgeCheck size={11} /> Diverifikasi tim</span>
  ) : (
    <span data-testid="my-review-status-published" className="inline-flex items-center gap-1.5 text-[11px] text-[#1E3A8A] bg-[#EFF6FF] border border-[#BFDBFE] rounded-full px-2.5 py-1">Tayang publik</span>
  );
}

export default function MyReviews() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { promptLogin(nav); return; }
    setLoading(true);
    api.get("/me/reviews")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Gagal memuat review Anda"))
      .finally(() => setLoading(false));
  }, [user, nav]);

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ratings: { ...(r.ratings || Object.fromEntries(RATING_KEYS.map(({ key }) => [key, 5]))) },
      body: r.body || "",
      pros: r.pros || "",
      cons: r.cons || "",
      is_anonymous: !!r.is_anonymous,
    });
  };
  const closeEdit = () => { setEditing(null); setForm(null); };

  const submitEdit = async () => {
    if (!editing || !form) return;
    if (!form.body.trim() || !form.pros.trim() || !form.cons.trim()) {
      toast.error("Cerita, Kelebihan, dan Perlu Ditingkatkan wajib diisi.");
      return;
    }
    try {
      setBusy(true);
      const r = await api.patch(`/reviews/${editing.review_id}`, form);
      setItems((prev) => prev.map((x) => x.review_id === editing.review_id
        ? { ...x, ...r.data, school_name: x.school_name, school_city: x.school_city, school_image_url: x.school_image_url }
        : x));
      toast.success("Review disimpan — akan tayang setelah tim memoderasi ulang");
      closeEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const g = { pending: [], published: [], rejected: [] };
    for (const r of items) (g[r.status] || g.published).push(r);
    return g;
  }, [items]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-12 text-[#4B5563]" data-testid="my-reviews-loading">Memuat review…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="my-reviews-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#4B5563]">Profil saya</div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1F2937] mt-2 leading-tight">Review Saya</h1>
        <p className="text-sm text-[#4B5563] mt-2 max-w-xl">Kelola dan perbarui review yang pernah Anda tulis. Setiap kali Anda mengedit, review akan otomatis kembali ke antrian moderasi sebelum tayang publik.</p>
      </div>

      {items.length === 0 ? (
        <div className="p-8 rounded-2xl bg-[#F3F1ED] border border-[#EAE7E0] text-center" data-testid="my-reviews-empty">
          <p className="text-[#4B5563]">Anda belum menulis review.</p>
          <Button asChild className="mt-4 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">
            <Link to="/sekolah" data-testid="my-reviews-explore-schools">Jelajahi sekolah <ArrowRight size={14} className="ml-1" /></Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r, i) => (
            <article key={r.review_id} data-testid={`my-review-card-${i}`}
              className="bg-white border border-[#EAE7E0] rounded-2xl p-5 hover:border-[#1E3A8A]/40 transition-colors">
              <div className="flex items-start gap-4">
                {r.school_image_url && (
                  <Link to={getSchoolUrl({ school_id: r.school_id, name: r.school_name, city: r.school_city })} className="w-20 h-14 rounded-xl overflow-hidden bg-[#F3F1ED] flex-shrink-0">
                    <img src={r.school_image_url} alt={r.school_name} className="w-full h-full object-cover" />
                  </Link>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{r.school_city}</div>
                      <Link to={getSchoolUrl({ school_id: r.school_id, name: r.school_name, city: r.school_city })} className="font-serif-display text-lg text-[#1F2937] hover:text-[#1E3A8A] transition-colors line-clamp-1" data-testid={`my-review-school-${i}`}>
                        {r.school_name || "Sekolah"}
                      </Link>
                    </div>
                    <StatusPill status={r.status} isVerified={r.is_verified} />
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#1F2937]">
                    <Star size={14} className="fill-[#E07A5F] text-[#E07A5F]" />
                    <span className="font-semibold">{avgRating(r.ratings)}</span>
                    <span className="text-[#4B5563]">·</span>
                    <span className="text-[#1F2937]" data-testid={`my-review-title-${i}`}>{r.title || "(tanpa judul)"}</span>
                    {r.is_anonymous && <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#4B5563]"><EyeOff size={10} /> Anonim</span>}
                  </div>
                  {r.body && <p className="mt-2 text-sm text-[#4B5563] leading-snug line-clamp-3">{r.body}</p>}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button size="sm" onClick={() => openEdit(r)} data-testid={`my-review-edit-${i}`}
                      className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-8 text-xs"><Pencil size={12} className="mr-1" /> Edit review</Button>
                    <Link to={getSchoolUrl({ school_id: r.school_id, name: r.school_name, city: r.school_city })} className="text-xs text-[#1E3A8A] hover:text-[#E07A5F] underline-offset-4 hover:underline">Lihat di halaman sekolah →</Link>
                    {r.edit_count > 0 && <span className="text-[10px] text-[#4B5563]">Diedit {r.edit_count}x</span>}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-6 text-xs text-[#4B5563]" data-testid="my-reviews-counts">
          {grouped.pending.length} menunggu moderasi · {grouped.published.length} tayang · {grouped.rejected.length} ditolak
        </div>
      )}

      {/* ---- Edit dialog ---- */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v && !busy) closeEdit(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="my-review-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit review · {editing?.school_name}</DialogTitle>
            <DialogDescription className="text-xs text-[#4B5563]">Review yang Anda edit akan kembali ke antrian moderasi sebelum tayang publik.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-[12px] text-[#92400E] flex items-start gap-2">
                <Clock size={14} className="mt-0.5 flex-shrink-0" /> Setelah Anda simpan, review akan kembali ke status <b>Menunggu moderasi</b> sampai admin memverifikasi ulang.
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Rating per kategori</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {RATING_KEYS.map(({ key, label }) => (
                    <div key={key} className="p-2.5 rounded-xl bg-[#FAFAF8] border border-[#EAE7E0] flex items-center justify-between">
                      <span className="text-sm text-[#1F2937]">{label}</span>
                      <div className="inline-flex items-center gap-1">
                        {[1,2,3,4,5].map((n) => (
                          <button key={n} type="button" data-testid={`my-review-edit-${key}-${n}`}
                            onClick={() => setForm({ ...form, ratings: { ...form.ratings, [key]: n } })}
                            className="p-0.5">
                            <Star size={16} className={n <= (form.ratings[key]||0) ? "fill-[#E07A5F] text-[#E07A5F]" : "text-[#D6D3D1]"} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Cerita utama</div>
                <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="my-review-edit-body" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#166534] mb-1.5 inline-flex items-center gap-1 font-semibold"><ThumbsUp size={11} /> Kelebihan</div>
                  <Textarea rows={3} value={form.pros} onChange={(e) => setForm({ ...form, pros: e.target.value })} data-testid="my-review-edit-pros"
                    className="rounded-xl border-[#BBF7D0] bg-[#F0FDF4]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#854D0E] mb-1.5 inline-flex items-center gap-1 font-semibold"><ThumbsDown size={11} /> Perlu Ditingkatkan</div>
                  <Textarea rows={3} value={form.cons} onChange={(e) => setForm({ ...form, cons: e.target.value })} data-testid="my-review-edit-cons"
                    className="rounded-xl border-[#FDE68A] bg-[#FEFCE8]" />
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer">
                <Checkbox checked={form.is_anonymous} onCheckedChange={(v) => setForm({ ...form, is_anonymous: !!v })} data-testid="my-review-edit-anon" />
                Sembunyikan nama saya (tampil sebagai "Anonim")
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={closeEdit} disabled={busy} className="rounded-full h-10">Batal</Button>
                <Button onClick={submitEdit} disabled={busy} data-testid="my-review-edit-submit"
                  className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">
                  {busy ? "Menyimpan…" : "Simpan & kirim ke moderasi"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function avgRating(ratings) {
  if (!ratings || typeof ratings !== "object") return "-";
  const vals = Object.values(ratings).filter((v) => typeof v === "number");
  if (!vals.length) return "-";
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}
